/**
 * DEMO-ONLY: session overlay for order-template share, optional lines, and product add/remove
 * (MVP 2.4.x saved carts on DIAS wishlists).
 *
 * Also persists demo-created saved carts by customer account so an Account Manager (AM)
 * cart created for Acme is visible to other Acme users (FR8.2.2 / MVP 2.4.1).
 */
import {
  getPresidioMockActiveCustomerNo,
  getPresidioMockActiveLogin,
  isPresidioMockSalesRepLogin,
  PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN,
  PRESIDIO_MOCK_APPROVER_LOGIN,
  PRESIDIO_MOCK_BUYER_LOGIN,
  PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS,
  PRESIDIO_MOCK_DEFAULT_LOGIN,
  PRESIDIO_MOCK_SALES_REP_LOGIN,
} from './presidio-mock-session';

export type PresidioOrderTemplateShareType = 'account_user' | 'sales';

export interface PresidioOrderTemplateShareRecipient {
  id: string;
  displayName: string;
  type: PresidioOrderTemplateShareType;
}

export interface PresidioOrderTemplateOverlay {
  sharedWith: PresidioOrderTemplateShareRecipient[];
  /** sku → optional */
  optionalBySku: Record<string, boolean>;
  /** sku → quantity (seed updates + newly added lines) */
  quantityBySku: Record<string, number>;
  /** skus removed from the seeded template */
  removedSkus: string[];
}

/** DEMO: session-created saved cart (wishlist) under a customer account. */
export interface PresidioCreatedOrderTemplate {
  id: string;
  customerNo: string;
  title: string;
  creationDate: number;
  createdByFirstName: string;
  createdByLastName: string;
  createdByBusinessPartnerNo: string;
  createdByLogin: string;
  /**
   * AM/sales-created carts for the active customer are visible to all users of that account
   * without a separate share step (on-behalf-of account).
   */
  accountVisible: boolean;
}

export const PRESIDIO_MOCK_ORDER_TEMPLATE_OVERLAY_KEY = 'presidio-mock-order-template-overlay';
export const PRESIDIO_MOCK_ORDER_TEMPLATES_CREATED_KEY = 'presidio-mock-order-templates-created';

/** Demo recipients for share picker (no emails in analytics). */
export const PRESIDIO_MOCK_SHARE_CANDIDATES: PresidioOrderTemplateShareRecipient[] = [
  { id: 'user-alex', displayName: 'Alex Rivera', type: 'account_user' },
  { id: 'user-sam', displayName: 'Sam Chen', type: 'account_user' },
  { id: 'sales-team', displayName: 'Sales team', type: 'sales' },
];

const emptyOverlay = (): PresidioOrderTemplateOverlay => ({
  sharedWith: [],
  optionalBySku: {},
  quantityBySku: {},
  removedSkus: [],
});

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const DEFAULT_OVERLAY: Record<string, PresidioOrderTemplateOverlay> = {
  'OT-001': {
    sharedWith: [{ id: 'sales-team', displayName: 'Sales team', type: 'sales' }],
    optionalBySku: { 'PRS-MOUSE-OPT': true },
    quantityBySku: {},
    removedSkus: [],
  },
  'OT-002': {
    sharedWith: [{ id: 'user-jordan', displayName: 'Jordan Hale', type: 'account_user' }],
    optionalBySku: {},
    quantityBySku: {},
    removedSkus: [],
  },
};

function normalizeOverlay(raw: Partial<PresidioOrderTemplateOverlay> | undefined): PresidioOrderTemplateOverlay {
  return {
    sharedWith: raw?.sharedWith || [],
    optionalBySku: raw?.optionalBySku || {},
    quantityBySku: raw?.quantityBySku || {},
    removedSkus: raw?.removedSkus || [],
  };
}

function readAll(): Record<string, PresidioOrderTemplateOverlay> {
  if (typeof sessionStorage === 'undefined') {
    return cloneJson(DEFAULT_OVERLAY);
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_ORDER_TEMPLATE_OVERLAY_KEY);
    if (raw === null) {
      writeAll(cloneJson(DEFAULT_OVERLAY));
      return cloneJson(DEFAULT_OVERLAY);
    }
    const parsed = JSON.parse(raw) as Record<string, Partial<PresidioOrderTemplateOverlay>>;
    return Object.fromEntries(Object.entries(parsed).map(([id, overlay]) => [id, normalizeOverlay(overlay)]));
  } catch {
    return cloneJson(DEFAULT_OVERLAY);
  }
}

function writeAll(map: Record<string, PresidioOrderTemplateOverlay>): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_ORDER_TEMPLATE_OVERLAY_KEY, JSON.stringify(map));
}

export function getPresidioOrderTemplateOverlay(id: string): PresidioOrderTemplateOverlay {
  const all = readAll();
  return normalizeOverlay(all[id] ?? emptyOverlay());
}

function updateOverlay(id: string, next: PresidioOrderTemplateOverlay): void {
  const all = readAll();
  all[id] = next;
  writeAll(all);
}

export function setPresidioOrderTemplateOptional(id: string, sku: string, optional: boolean): void {
  const current = getPresidioOrderTemplateOverlay(id);
  updateOverlay(id, {
    ...current,
    optionalBySku: { ...current.optionalBySku, [sku]: optional },
  });
}

export function addPresidioOrderTemplateShare(
  id: string,
  recipient: PresidioOrderTemplateShareRecipient
): PresidioOrderTemplateShareRecipient[] {
  const current = getPresidioOrderTemplateOverlay(id);
  const sharedWith = current.sharedWith.filter(r => r.id !== recipient.id);
  sharedWith.push(recipient);
  updateOverlay(id, { ...current, sharedWith });
  return sharedWith;
}

export function removePresidioOrderTemplateShare(
  id: string,
  recipientId: string
): PresidioOrderTemplateShareRecipient[] {
  const current = getPresidioOrderTemplateOverlay(id);
  const sharedWith = current.sharedWith.filter(r => r.id !== recipientId);
  updateOverlay(id, { ...current, sharedWith });
  return sharedWith;
}

/** DEMO-ONLY: add or adjust a product line (quantity is a delta, matching DIAS addProductToOrderTemplate). */
export function addPresidioOrderTemplateProduct(id: string, sku: string, quantity: number): void {
  const delta = Number(quantity) || 0;
  const current = getPresidioOrderTemplateOverlay(id);
  const wasRemoved = current.removedSkus.includes(sku);
  const quantityBySku = { ...current.quantityBySku };
  if (wasRemoved) {
    quantityBySku[sku] = Math.max(1, delta || 1);
  } else {
    quantityBySku[sku] = (quantityBySku[sku] || 0) + delta;
  }
  updateOverlay(id, {
    ...current,
    removedSkus: current.removedSkus.filter(s => s !== sku),
    quantityBySku,
  });
}

/** DEMO-ONLY: remove a product line from a saved cart / order template. */
export function removePresidioOrderTemplateProduct(id: string, sku: string): void {
  const current = getPresidioOrderTemplateOverlay(id);
  const quantityBySku = { ...current.quantityBySku };
  delete quantityBySku[sku];
  const removedSkus = current.removedSkus.includes(sku) ? current.removedSkus : [...current.removedSkus, sku];
  const optionalBySku = { ...current.optionalBySku };
  delete optionalBySku[sku];
  updateOverlay(id, { ...current, quantityBySku, removedSkus, optionalBySku });
}

type WishlistItem = { attributes: { name: string; value: unknown }[] };

function itemSku(item: WishlistItem): string {
  const skuAttr = (item.attributes || []).find(a => a.name === 'sku');
  return skuAttr ? String(skuAttr.value) : '';
}

function withQuantity(item: WishlistItem, quantity: number): WishlistItem {
  const attrs = (item.attributes || []).filter(a => a.name !== 'desiredQuantity');
  attrs.push({ name: 'desiredQuantity', value: { value: quantity } });
  return { ...item, attributes: attrs };
}

function withOptional(item: WishlistItem, optional: boolean | undefined): WishlistItem {
  if (optional === undefined) {
    return item;
  }
  const attrs = (item.attributes || []).filter(a => a.name !== 'optional');
  attrs.push({ name: 'optional', value: optional });
  return { ...item, attributes: attrs };
}

function newItem(sku: string, quantity: number): WishlistItem {
  return {
    attributes: [
      { name: 'sku', value: sku },
      { name: 'id', value: `oti-mock-${sku}` },
      { name: 'creationDate', value: Date.now() },
      { name: 'desiredQuantity', value: { value: quantity } },
      { name: 'optional', value: false },
    ],
  };
}

/** Merge overlay into ICM-shaped wishlist GET body. */
export function applyPresidioOrderTemplateOverlay(id: string, body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const overlay = getPresidioOrderTemplateOverlay(id);
  const data = body as {
    sharedWith?: PresidioOrderTemplateShareRecipient[];
    items?: WishlistItem[];
    itemsCount?: number;
    editable?: boolean;
  };

  const removed = new Set(overlay.removedSkus);
  const seedSkus = new Set<string>();
  let items: WishlistItem[] = (data.items || [])
    .filter(item => {
      const sku = itemSku(item);
      if (!sku || removed.has(sku)) {
        return false;
      }
      seedSkus.add(sku);
      return true;
    })
    .map(item => {
      const sku = itemSku(item);
      const seedQtyAttr = (item.attributes || []).find(a => a.name === 'desiredQuantity');
      const seedQty =
        typeof seedQtyAttr?.value === 'object' && seedQtyAttr.value
          ? Number((seedQtyAttr.value as { value?: number }).value ?? 0)
          : 0;
      const delta = overlay.quantityBySku[sku] || 0;
      const nextQty = seedQty + delta;
      let next = nextQty !== seedQty ? withQuantity(item, Math.max(0, nextQty)) : item;
      if (nextQty <= 0) {
        return null;
      }
      return withOptional(next, overlay.optionalBySku[sku]);
    })
    .filter((item): item is WishlistItem => !!item);

  Object.entries(overlay.quantityBySku).forEach(([sku, quantity]) => {
    if (seedSkus.has(sku) || removed.has(sku) || quantity <= 0) {
      return;
    }
    items = [...items, withOptional(newItem(sku, quantity), overlay.optionalBySku[sku])];
  });

  return {
    ...data,
    sharedWith: overlay.sharedWith,
    items,
    itemsCount: items.length,
  };
}

function readCreated(): PresidioCreatedOrderTemplate[] {
  if (typeof sessionStorage === 'undefined') {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_ORDER_TEMPLATES_CREATED_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as PresidioCreatedOrderTemplate[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCreated(list: PresidioCreatedOrderTemplate[]): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_ORDER_TEMPLATES_CREATED_KEY, JSON.stringify(list));
}

function resolveCreatedAuthor(): {
  firstName: string;
  lastName: string;
  businessPartnerNo: string;
  login: string;
} {
  const login = (getPresidioMockActiveLogin() || '').trim();
  if (isPresidioMockSalesRepLogin(login)) {
    return {
      firstName: 'Riley',
      lastName: 'Sales',
      businessPartnerNo: 'BP-SALES-001',
      login: PRESIDIO_MOCK_SALES_REP_LOGIN,
    };
  }
  const known = PRESIDIO_MOCK_CSR_IMPERSONATION_TARGETS.find(t => t.login.toLowerCase() === login.toLowerCase());
  if (known) {
    return {
      firstName: known.firstName,
      lastName: known.lastName,
      businessPartnerNo: 'BP-MOCK-001',
      login: known.login,
    };
  }
  const local = login.includes('@') ? login.split('@')[0] : login || 'demo';
  const parts = local.split(/[._-]/).filter(Boolean);
  return {
    firstName: parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : 'Demo',
    lastName: parts[1] ? parts[1].charAt(0).toUpperCase() + parts[1].slice(1) : 'User',
    businessPartnerNo: 'BP-MOCK-001',
    login: login || 'demo@example.com',
  };
}

/** Map active login → share-recipient id used in overlays / pickers. */
export function presidioOrderTemplateShareIdForLogin(login?: string | null): string | undefined {
  const active = ((login ?? getPresidioMockActiveLogin()) || '').trim().toLowerCase();
  if (!active) {
    return undefined;
  }
  if (active === PRESIDIO_MOCK_DEFAULT_LOGIN.toLowerCase()) {
    return 'user-jordan';
  }
  if (active === PRESIDIO_MOCK_BUYER_LOGIN.toLowerCase()) {
    return 'user-alex';
  }
  if (active === PRESIDIO_MOCK_ACCOUNT_OWNER_LOGIN.toLowerCase() || active === 'sam.chen@acme.com') {
    return 'user-sam';
  }
  if (active === PRESIDIO_MOCK_APPROVER_LOGIN.toLowerCase()) {
    return 'user-morgan';
  }
  if (active === PRESIDIO_MOCK_SALES_REP_LOGIN.toLowerCase()) {
    return 'sales-team';
  }
  return undefined;
}

export function isPresidioCreatedOrderTemplateVisible(
  template: PresidioCreatedOrderTemplate,
  viewerLogin?: string | null
): boolean {
  const login = ((viewerLogin ?? getPresidioMockActiveLogin()) || '').trim().toLowerCase();
  if (!login) {
    return false;
  }
  if (template.createdByLogin.toLowerCase() === login) {
    return true;
  }
  if (template.accountVisible) {
    return template.customerNo === getPresidioMockActiveCustomerNo();
  }
  const shareId = presidioOrderTemplateShareIdForLogin(login);
  if (!shareId) {
    return false;
  }
  return getPresidioOrderTemplateOverlay(template.id).sharedWith.some(r => r.id === shareId);
}

export function getPresidioCreatedOrderTemplate(id: string): PresidioCreatedOrderTemplate | undefined {
  return readCreated().find(t => t.id === id);
}

export function listVisiblePresidioCreatedOrderTemplates(
  customerNo?: string,
  viewerLogin?: string | null
): PresidioCreatedOrderTemplate[] {
  const cust = (customerNo || getPresidioMockActiveCustomerNo()).trim();
  return readCreated().filter(
    t => t.customerNo === cust && isPresidioCreatedOrderTemplateVisible(t, viewerLogin)
  );
}

export function createPresidioOrderTemplate(title: string): {
  type: string;
  uri: string;
  title: string;
  itemId: string;
} {
  const name = (title || '').trim() || 'Saved cart';
  const author = resolveCreatedAuthor();
  const customerNo = getPresidioMockActiveCustomerNo();
  const id = `OT-NEW-${Date.now()}`;
  const accountVisible = isPresidioMockSalesRepLogin(author.login);
  const created: PresidioCreatedOrderTemplate = {
    id,
    customerNo,
    title: name,
    creationDate: Date.now(),
    createdByFirstName: author.firstName,
    createdByLastName: author.lastName,
    createdByBusinessPartnerNo: author.businessPartnerNo,
    createdByLogin: author.login,
    accountVisible,
  };
  writeCreated([...readCreated(), created]);
  return {
    type: 'Link',
    uri: `inSPIRED-inTRONICS_Business-Site/-/customers/-/users/-/wishlists/${id}`,
    title: id,
    itemId: id,
  };
}

export function updatePresidioCreatedOrderTemplateTitle(
  id: string,
  title: string
): PresidioCreatedOrderTemplate | undefined {
  const list = readCreated();
  const idx = list.findIndex(t => t.id === id);
  if (idx < 0) {
    return undefined;
  }
  const nextTitle = (title || '').trim() || list[idx].title;
  list[idx] = { ...list[idx], title: nextTitle };
  writeCreated(list);
  return list[idx];
}

export function deletePresidioCreatedOrderTemplate(id: string): boolean {
  const list = readCreated();
  const next = list.filter(t => t.id !== id);
  if (next.length === list.length) {
    return false;
  }
  writeCreated(next);
  return true;
}

/** ICM-shaped wishlist detail for a session-created saved cart (+ overlay). */
export function buildPresidioCreatedOrderTemplateBody(id: string): Record<string, unknown> | undefined {
  const created = getPresidioCreatedOrderTemplate(id);
  if (!created) {
    return undefined;
  }
  const login = (getPresidioMockActiveLogin() || '').trim().toLowerCase();
  const editable = created.createdByLogin.toLowerCase() === login;
  const base = {
    title: created.title,
    itemsCount: 0,
    creationDate: created.creationDate,
    createdByFirstName: created.createdByFirstName,
    createdByLastName: created.createdByLastName,
    createdByBusinessPartnerNo: created.createdByBusinessPartnerNo,
    editable,
    sharedWith: [] as PresidioOrderTemplateShareRecipient[],
    items: [] as WishlistItem[],
  };
  return applyPresidioOrderTemplateOverlay(id, base) as Record<string, unknown>;
}

/** Append visible session-created carts onto a wishlist list GET body. */
export function mergePresidioCreatedOrderTemplateLinks(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const data = body as { elements?: { type?: string; uri?: string; title?: string; itemId?: string }[] };
  const existing = Array.isArray(data.elements) ? [...data.elements] : [];
  const existingIds = new Set(
    existing.map(el => el.itemId || (el.title && /^OT-/i.test(el.title) ? el.title : '')).filter(Boolean)
  );
  const createdLinks = listVisiblePresidioCreatedOrderTemplates().map(t => ({
    type: 'Link',
    uri: `inSPIRED-inTRONICS_Business-Site/-/customers/-/users/-/wishlists/${t.id}`,
    title: t.id,
    itemId: t.id,
  }));
  const appended = createdLinks.filter(link => !existingIds.has(link.itemId));
  if (!appended.length) {
    return body;
  }
  return { ...data, elements: [...existing, ...appended] };
}

export function resetPresidioCreatedOrderTemplates(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_ORDER_TEMPLATES_CREATED_KEY);
}
